import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  Info,
  PlayCircle,
  Settings,
  CheckCircle,
  Check,
  Bell,
  ExternalLink,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import TopNav from "../components/TopNav";
import LessonIcon from "../components/LessonIcon";
import sectionIconMap from "../utils/sectionIconMap";
import { useGetModulesQuery } from "../redux/Clinictrainingapi";

// Shared card wrapper used by every layout variant
const PageCard = ({ children }) => (
  <div className="flex justify-center px-4 sm:px-6 py-6 sm:py-8">
    <motion.div
      className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 p-5 sm:p-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  </div>
);

// Shared "Start Module" button
const StartButton = ({ onClick, label = "Start Module" }) => (
  <div className="flex justify-center mt-8 sm:mt-10">
    <button
      onClick={onClick}
      className="flex items-center gap-2.5 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-dark hover:to-primary text-white px-8 sm:px-10 py-3.5 rounded-full font-semibold text-sm transition-all cursor-pointer shadow-lg shadow-primary/25 w-full sm:w-auto justify-center"
    >
      {label}
      <PlayCircle size={18} />
    </button>
  </div>
);

// Shared callout/info card
const Callout = ({ icon, label, body, delay = 0.5 }) => (
  <motion.div
    className="bg-cyan-50/60 rounded-xl p-4 sm:p-5 flex items-start gap-3"
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    {icon}
    <div>
      {label && (
        <div className="text-xs font-bold text-primary tracking-widest uppercase mb-1">
          {label}
        </div>
      )}
      <p className="text-sm text-gray-700 font-medium leading-relaxed">{body}</p>
    </div>
  </motion.div>
);

// Shared module badge
const ModuleBadge = ({ order }) => (
  <span className="text-xs font-bold text-primary border border-primary/30 bg-primary/5 rounded-md px-2.5 py-1 tracking-wide">
    MODULE {order}
  </span>
);

// Shared intro grid (list items with icons)
const IntroGrid = ({ items, startDelay = 0.2 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 sm:mb-8">
    {items.map((item, i) => (
      <motion.div
        key={i}
        className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 sm:px-5 py-3.5 sm:py-4 border border-gray-100"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: startDelay + i * 0.08 }}
      >
        <LessonIcon
          name={sectionIconMap[item] ?? "CheckCircle"}
          size={20}
          className="text-primary/60 shrink-0"
        />
        <span className="text-sm text-gray-800 font-medium">{item}</span>
      </motion.div>
    ))}
  </div>
);

const MODULE_ESTIMATED_TIMES = {
  1: "15 minutes",
  2: "25 minutes",
  3: "25 minutes",
  4: "15 minutes",
  5: "10 minutes",
  6: "10 minutes",
};

export default function ModuleIntro() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: modules } = useGetModulesQuery();

  // Always scroll to top on mount / module change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [moduleId]);

  const module = modules?.find((m) => m.order === Number(moduleId));
  if (!module) return <div className="p-8">Loading...</div>;

  const intro = module.sections[0];
  const introItems = intro?.list_items
    ? intro.list_items.split("\n").filter(Boolean)
    : [];

  const isFinalModule = !module.knowledge_checks?.length;

  const startModule = () => {
    const firstLesson =
      module.sections.find((s) => s.order > 1) ?? module.sections[1];
    if (firstLesson) {
      navigate(`/module/${module.order}/lesson/${firstLesson.order}`);
    }
  };

  const completionSummary = module.sections
    .filter((s) => s.order > 1)
    .map((s) => s.heading)
    .slice(0, 6);

  const isCompleted = searchParams.get("completed") === "true";

  // ── Shared TopNav wrapper for all variants ──────────────────────────
  const SharedNav = ({ breadcrumbs, rightContent, backTo = "/dashboard" }) => (
    <TopNav
      backTo={backTo}
      backLabel="BACK TO CURRICULUM"
      breadcrumbs={breadcrumbs}
      rightContent={
        <span className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-primary">
          {rightContent}
        </span>
      }
    />
  );

  /* ── FINAL MODULE COMPLETION SCREEN ─────────────────────────────── */
  if (isFinalModule && isCompleted) {
    return (
      <div className="min-h-screen bg-surface">
        <Sidebar />
        <div className="ml-0 md:ml-16">
          <SharedNav
            breadcrumbs={[{ label: "COMPLIANCE TRAINING", to: "/dashboard" }, "MILESTONE REACHED"]}
            rightContent={
              <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                <CheckCircle size={14} /> ALL MODULES COMPLETE
              </span>
            }
          />

          <div className="flex justify-center px-4 sm:px-6 py-8 sm:py-12">
            <motion.div
              className="w-full max-w-2xl text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Success icon */}
              <motion.div
                className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                <div className="relative w-full h-full rounded-full bg-primary mx-auto flex items-center justify-center shadow-lg shadow-primary/30">
                  <Check size={36} className="text-white" strokeWidth={3} />
                </div>
              </motion.div>

              <motion.div
                className="text-xs font-bold text-primary tracking-widest uppercase mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                MILESTONE REACHED
              </motion.div>

              <motion.h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                YOU HAVE COMPLETED
                <br />
                MODULE {module.order}
              </motion.h1>

              {/* Learning Summary */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 mb-6 sm:mb-8 text-left"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
              >
                <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4 sm:mb-6">
                  LEARNING SUMMARY
                </div>
                <p className="text-gray-900 font-medium mb-4">You now understand:</p>
                {/* Single col on mobile, 2 cols on sm+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {completionSummary.map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.7 + i * 0.1 }}
                    >
                      <CheckCircle size={18} className="text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Module stepper — wrap on small screens */}
              <motion.div
                className="flex items-center justify-center flex-wrap gap-y-2 mb-6 sm:mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.9 }}
              >
                {(modules ?? []).map((m, i) => (
                  <div key={m.id} className="flex items-center">
                    <motion.div
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold bg-primary text-white shadow-lg shadow-primary/30"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: 1 + i * 0.1 }}
                    >
                      {m.order}
                    </motion.div>
                    {i < (modules?.length ?? 0) - 1 && (
                      <div className="w-5 sm:w-8 h-0.5 bg-primary" />
                    )}
                  </div>
                ))}
              </motion.div>

              {/* Final Exam CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1.2 }}
              >
                <button
                  onClick={() => navigate("/final-exam")}
                  className="w-full max-w-md mx-auto flex items-center justify-center gap-3 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-dark hover:to-primary text-white px-8 py-3.5 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all cursor-pointer shadow-lg shadow-primary/25 mb-4"
                >
                  Start Final Exam
                  <ExternalLink size={18} />
                </button>
                <p className="text-xs text-gray-400 px-4">
                  Ready to earn your HIPAA Certification? The final exam covers all modules.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  /* ── MODULE 4: Special Breach Intro ─────────────────────────────── */
  if (module.order === 4) {
    return (
      <div className="min-h-screen bg-surface">
        <Sidebar />
        <div className="ml-0 md:ml-16">
          <SharedNav
            breadcrumbs={[{ label: "COMPLIANCE TRAINING", to: "/dashboard" }, `MODULE ${module.order}`]}
            rightContent={<>FOCUS MODE READY <Settings size={14} /></>}
          />
          <PageCard>
            <div className="flex items-center gap-3 mb-4">
              <ModuleBadge order={module.order} />
              {MODULE_ESTIMATED_TIMES[module.order] && (
                <span className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Clock size={12} />
                  Estimated Time: {MODULE_ESTIMATED_TIMES[module.order]}
                </span>
              )}
            </div>

            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-3">
              {module.title}
            </h1>
            <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              {intro?.body}
            </p>

            {/* Rule Card */}
            <motion.div
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Bell size={20} className="text-primary sm:hidden" />
                  <Bell size={24} className="text-primary hidden sm:block" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                    This module is based on the: HIPAA Breach Notification Rule.
                  </p>
                  <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-3">
                    THIS RULE EXPLAINS:
                  </div>
                  <div className="space-y-2">
                    {introItems.map((item, i) => (
                      <motion.div
                        key={i}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.25, delay: 0.3 + i * 0.08 }}
                      >
                        <CheckCircle size={16} className="text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {intro?.callout && (
              <Callout
                icon={
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">!</span>
                  </div>
                }
                label="YOUR RESPONSIBILITY IS SIMPLE:"
                body={intro.callout}
              />
            )}

            <StartButton onClick={startModule} />
          </PageCard>
        </div>
      </div>
    );
  }

  /* ── FINAL MODULE INTRO (before completed) ───────────────────────── */
  if (isFinalModule) {
    return (
      <div className="min-h-screen bg-surface">
        <Sidebar />
        <div className="ml-0 md:ml-16">
          <SharedNav
            breadcrumbs={[{ label: "COMPLIANCE TRAINING", to: "/dashboard" }, `MODULE ${module.order}`]}
            rightContent={<>FOCUS MODE READY <Settings size={14} /></>}
          />
          <PageCard>
            <div className="flex items-center gap-3 mb-4">
              <ModuleBadge order={module.order} />
              {MODULE_ESTIMATED_TIMES[module.order] && (
                <span className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Clock size={12} />
                  Estimated Time: {MODULE_ESTIMATED_TIMES[module.order]}
                </span>
              )}
            </div>

            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-3">
              {module.title}
            </h1>
            <p className="text-sm text-gray-500 mb-4 sm:mb-6">
              Goal: {module.objective}
            </p>
            <p className="text-gray-500 leading-relaxed mb-5 sm:mb-6 text-sm sm:text-base">
              {intro?.body}
            </p>

            <IntroGrid items={introItems} />

            {intro?.callout && (
              <Callout
                icon={
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary flex items-center justify-center shrink-0">
                    <Check size={18} className="text-white" />
                  </div>
                }
                label="IMPACT:"
                body={intro.callout}
              />
            )}

            <StartButton onClick={startModule} />
          </PageCard>
        </div>
      </div>
    );
  }

  /* ── DEFAULT LAYOUT — Modules 1, 2, 3, 5 ───────────────────────── */
  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />
      <div className="ml-0 md:ml-16">
        <SharedNav
          breadcrumbs={[{ label: "INTRODUCTION", to: "/dashboard" }, `MODULE ${module.order}.1`]}
          rightContent={<>FOCUS MODE READY <Settings size={14} /></>}
        />
        <PageCard>
          <div className="flex items-center gap-3 mb-4">
            <ModuleBadge order={module.order} />
            {MODULE_ESTIMATED_TIMES[module.order] && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <Clock size={12} />
                Estimated Time: {MODULE_ESTIMATED_TIMES[module.order]}
              </span>
            )}
          </div>

          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-3">
            MODULE {module.order}: {module.title}
          </h1>
          <p className="text-sm text-gray-500 mb-6 sm:mb-8">
            Objective: {module.objective}
          </p>

          <div className="text-xs font-bold text-primary tracking-widest uppercase mb-3">
            {intro?.subtitle || "Introduction"}
          </div>
          <p className="text-gray-500 leading-relaxed mb-5 sm:mb-6 text-sm sm:text-base">
            {intro?.body}
          </p>

          <IntroGrid items={introItems} />

          {/* Scenario card */}
          {module.scenarios?.[0] && (
            <motion.div
              className="bg-cyan-50/60 rounded-xl p-4 sm:p-5 flex items-start gap-3"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Info size={18} className="text-primary shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-gray-900 mb-1">
                  {module.scenarios[0].content}
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {module.scenarios[0].high_impact_quote}
                </p>
              </div>
            </motion.div>
          )}

          {/* Fallback callout */}
          {!module.scenarios?.[0] && intro?.callout && (
            <motion.div
              className="bg-cyan-50/60 rounded-xl p-4 sm:p-5 flex items-start gap-3"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Info size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-gray-500 leading-relaxed">{intro.callout}</p>
            </motion.div>
          )}

          <StartButton onClick={startModule} />
        </PageCard>
      </div>
    </div>
  );
}